import { gql } from '@apollo/client';
import { GetStaticPaths, GetStaticProps } from 'next';
import { rehype } from 'rehype';
import rehypeAutolinkHeadings from 'rehype-autolink-headings';
import rehypeSlug from 'rehype-slug';
import { toString } from 'hast-util-to-string';
import BlogPostComponent from '../../components/elements/BlogPost';
import PageLayout, { MenuProps } from '../../layouts/Page';
import { BLOG_POST, GET_RELATED_POSTS, GET_REMAINING_POSTS } from '../../queries/blog';
import {
  Author,
  Blog,
  BlogBlogDynamicDynamicZone,
  ComponentComponentsRichtext,
  Event,
  UploadFile,
} from '../../types/schema';
import { initializeApollo } from '../../utils/apollo';
import { Error, sendError } from '../../utils/error';
import { flatten, orderDataByRichTextId, translateProperty } from '../../utils/object';

const rehypeProcessor = rehype()
  .data('settings', { fragment: true })
  .use(rehypeSlug)
  .use(rehypeAutolinkHeadings, {
    behavior: 'append',
    headingProperties: { className: 'heading-anchor-parent' },
    properties: (node) => ({
      className: 'heading-anchor-link',
      'aria-label': `Link direto para: ${toString(node)}`,
      title: `Link direto para: ${toString(node)}`,
    }),
    test: ['h1', 'h2', 'h3', 'h4'],
    content: (node) => [
      {
        type: 'element',
        tagName: 'span',
        properties: { className: 'sr-only' },
        children: [{ type: 'text', value: `Link direto para: ${toString(node)}` }],
      },
      {
        type: 'element',
        tagName: 'svg',
        properties: {
          className: 'heading-anchor-icon',
          xmlns: 'http://www.w3.org/2000/svg',
          viewBox: '0 0 24 24',
          width: '0.6em',
          height: '0.6em',
          fill: 'none',
          stroke: 'currentColor',
          strokeWidth: '2',
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
          'aria-hidden': 'true',
        },
        children: [
          {
            type: 'element',
            tagName: 'path',
            properties: {
              d: 'M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71',
            },
            children: [],
          },
          {
            type: 'element',
            tagName: 'path',
            properties: {
              d: 'M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71',
            },
            children: [],
          },
        ],
      },
    ],
  });

function processRichTextAnchors(
  blogDynamic: BlogBlogDynamicDynamicZone[],
): BlogBlogDynamicDynamicZone[] {
  return blogDynamic.map((item) => {
    if (item.__typename === 'ComponentComponentsRichtext') {
      const richtext = item as ComponentComponentsRichtext;
      if (richtext.body) {
        const processed = rehypeProcessor.processSync(richtext.body);
        return { ...richtext, body: String(processed) };
      }
    }
    return item;
  });
}

type BlogPostContainerProps = {
  post: Blog;
  relatedPosts: Blog[];
  menus: MenuProps;
  locale: string;
  events: Event[];
};

export const config = {
  maxDuration: 300,
};

const BlogPostContainer: React.FC<BlogPostContainerProps> = ({
  post,
  relatedPosts,
  menus,
  events,
  locale,
}) => {
  return (
    <PageLayout menus={menus} locale={locale}>
      <BlogPostComponent
        metaTitle={post?.metadata?.metaTitle as string}
        hideAppMastersTitle={post?.metadata?.hideAppMastersTitle as boolean}
        metaDescription={post?.metadata?.metaDescription as string}
        metaImage={(post.metadata?.metaImage as unknown as { url?: string })?.url as string}
        description={post.description as string}
        featuredImage={post.featuredImage as UploadFile}
        language={post.locale as string}
        publicationDate={post.publicationDate}
        postAuthor={post.author as Author}
        relatedPosts={relatedPosts}
        slug={post.slug as string}
        title={post.title as string}
        date={post.publishedAt}
        updatedAt={post.updatedAt}
        createdAt={post.createdAt}
        blogDynamic={post.blogDynamic as BlogBlogDynamicDynamicZone[]}
        events={events}
      />
    </PageLayout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  const apolloClient = initializeApollo();

  const { data } = await apolloClient.query({
    query: gql`
      query GET_POST_ROUTES {
        blogs(locale: "*") {
          slug
          locale
        }
      }
    `,
  });
  const paths = data.blogs?.map((blog: Blog) => {
    return {
      params: { slug: blog.slug },
      locale: blog.locale,
    };
  });
  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  const { slug } = ctx.params as { slug: string };
  const { locale } = ctx;
  const apolloClient = initializeApollo();

  try {
    const { data: dataBlogPosts } = await apolloClient.query({
      query: BLOG_POST,
      variables: { slug, locale },
    });

    const newDataBlogPosts = flatten(dataBlogPosts) as {
      [key: string]: Record<string, unknown> | Blog[];
    };

    if (locale !== (newDataBlogPosts as { blogs: Blog[] })?.blogs?.[0]?.locale) {
      return {
        notFound: true,
      };
    }

    const { data: dataRelatedPosts } = await apolloClient.query({
      query: GET_RELATED_POSTS,
      variables: {
        slug,
        locale,
      },
    });
    const newDataRelatedPosts = flatten(dataRelatedPosts) as { allRelatedPosts: Blog[] };

    const length = newDataRelatedPosts.allRelatedPosts.length;
    let relatedPosts = newDataRelatedPosts.allRelatedPosts;
    if (length < 3) {
      const notTheseSlugs = newDataRelatedPosts.allRelatedPosts
        .map((post: Blog) => post.slug)
        .concat(slug);
      const { data: remainingPosts } = await apolloClient.query({
        query: GET_REMAINING_POSTS,
        variables: {
          locale,
          limit: 3 - length,
          notTheseSlugs,
        },
      });
      relatedPosts = relatedPosts.concat(
        (flatten(remainingPosts) as { remainingPosts: Blog[] }).remainingPosts,
      );
    }

    const translatedObj = translateProperty(
      (newDataBlogPosts as { blogs: Blog[] }).blogs[0],
      ['backgroundColor', 'background', 'content', 'cardBackground', 'limit'],
      'blogDynamic',
    );

    const newDz = orderDataByRichTextId(
      (translatedObj as { blogDynamic: BlogBlogDynamicDynamicZone[] }).blogDynamic,
      {
        page: `/blog/${slug}`,
        slug: slug,
        locale: locale,
      },
    );

    const processedDz = processRichTextAnchors(newDz);

    return {
      props: {
        post: { ...translatedObj, blogDynamic: processedDz } as Blog,
        relatedPosts,
        events: newDataBlogPosts.events || [],
        locale,
        menus: {
          header: newDataBlogPosts.header,
          technologies: newDataBlogPosts.technologies,
          services: newDataBlogPosts.services,
          footerPt: newDataBlogPosts.footerPt,
          footerEn: newDataBlogPosts.footerEn,
        },
      },
    };
  } catch (error) {
    sendError({ error: error as Error, slug, message: 'Error on blog/[slug]' });
    return {
      notFound: true,
    };
  }
};

export default BlogPostContainer;
